import { Dayjs } from 'dayjs';

import {
  CareTaskComponent,
  CareTaskWithAnswer,
  ComponentAnswer,
  ComponentTypes,
  ExpectedComponentAnswerObjectByBackend,
  TasksSummaryItem,
} from '@type';
import {
  dateIsBeforeNow,
  formatTaskTime,
  isTaskExpiringSoon,
  unixEpochsToTimezoneDate,
} from '@utils';

export const getTaskKey = (task: TasksSummaryItem) => {
  return `${task.siteId}/${task.careplanId}/${task.occurrenceId}`;
};

const calculateTaskTimingInfo = (
  occurrenceDate: Dayjs,
  occurrenceEndDate: Dayjs
) => {
  const isExpireSoon = isTaskExpiringSoon(occurrenceDate, occurrenceEndDate);
  const isStartedInPast = dateIsBeforeNow(occurrenceDate);
  const taskTime = formatTaskTime(
    isStartedInPast ? occurrenceEndDate : occurrenceDate
  );
  const isExpired = taskTime === null;

  return {
    isExpireSoon,
    isStartedInPast,
    taskTime,
    isExpired,
  };
};

export const getTimelyUpdatedTask = (
  task: CareTaskWithAnswer
): CareTaskWithAnswer => {
  const { occurrenceDate, occurrenceEndDate } = task.template;

  const { isExpireSoon, isStartedInPast, taskTime, isExpired } =
    calculateTaskTimingInfo(occurrenceDate, occurrenceEndDate);

  return {
    ...task,
    template: {
      ...task.template,
      isExpireSoon,
      isStartedInPast,
      taskTime,
      isExpired,
    },
  };
};

export const formatNewTaskToBeAdded = (
  task: TasksSummaryItem
): CareTaskWithAnswer => {
  const occurrenceDate = unixEpochsToTimezoneDate(task.startUnixEpochs);
  const occurrenceEndDate = unixEpochsToTimezoneDate(task.endUnixEpochs);

  const { isExpireSoon, isStartedInPast, taskTime, isExpired } =
    calculateTaskTimingInfo(occurrenceDate, occurrenceEndDate);

  return {
    key: getTaskKey(task),
    siteId: task.siteId,
    siteUuid: task.siteUuid,
    template: {
      id: task.careplanId,
      name: task.name,
      asset: task.asset,
      siteId: task.siteId,
      occurrenceDate,
      occurrenceEndDate,
      validOccurrence: {
        id: task.occurrenceId,
        careplanId: task.careplanId,
        unixEpochs: task.startUnixEpochs,
      },
      isExpireSoon,
      isStartedInPast,
      taskTime,
      isExpired,
    },
    lastAnsweredIndex: 0,
    lastSeenCompIndex: 0,
    progressPercentage: 0,
    answer: null,
  };
};

export const getComponentAnswer = (
  answer: ComponentAnswer
): ExpectedComponentAnswerObjectByBackend => {
  const answerFullObject: ExpectedComponentAnswerObjectByBackend = {
    ...answer,
  };
  if (answer.answer === null) return answerFullObject;

  switch (answer.careplanComponentTypeId) {
    case ComponentTypes.FreeText:
      answerFullObject.freetext = answer.answer as string;
      break;
    case ComponentTypes.SingleChoice:
      answerFullObject.singleChoice = answer.answer as number;
      break;
    case ComponentTypes.MultipleChoice:
      answerFullObject.multiChoice = answer.answer as Record<string, boolean>;
      break;
    case ComponentTypes.YesNo:
    case ComponentTypes.Section:
      answerFullObject.yesno = answer.answer as number;
      break;
    case ComponentTypes.NumericValue:
      answerFullObject.numericalValue = answer.answer as number;
      break;
    case ComponentTypes.NumericRange:
      answerFullObject.numericRangeValue = answer.answer as number;
      break;
    case ComponentTypes.Bmi:
      Object.assign(
        answerFullObject,
        answer.answer as { height: string; weight: string }
      );
      break;
    case ComponentTypes.BloodPressure:
      Object.assign(
        answerFullObject,
        answer.answer as { systolic: number; diastolic: number }
      );
      break;
    case ComponentTypes.PainLocationChart: {
      const answerDetail = answer.answer as {
        selectedBodyPart: string;
        x: number;
        y: number;
        isFaceForward: boolean;
      };

      answerFullObject.bodychart = answerDetail.selectedBodyPart;
      answerFullObject.back = !answerDetail.isFaceForward;
      answerFullObject.painLocationChart = {
        x: answerDetail.x,
        y: answerDetail.y,
      };
      break;
    }
    case ComponentTypes.Ranking: {
      const answerDetail = answer.answer as { order: number[] };
      answerFullObject.multiChoice = { order: answerDetail.order };
      break;
    }
    case ComponentTypes.FileRequest:
      answerFullObject.file = answer.answer as {
        directLink: string;
        fileName: string;
        size: number;
      };
      break;
    case ComponentTypes.DateRequest:
      answerFullObject.dateValue = answer.answer as string;
      break;
  }
  return answerFullObject;
};

export const getAnswerObjectFromComponent = (
  components: CareTaskComponent[]
) => {
  const answersObject: ComponentAnswer[] = [];
  components.forEach((component: CareTaskComponent) => {
    answersObject.push({
      answer: null,
      careplanCareplanComponentMapperId:
        component.careplanCareplanComponentMapper.id,
      careplanComponentId: component.id,
      careplanComponentTypeId: component.careplanComponentTypeId,
      careplanComponentSequence: component.careplanCareplanComponentMapper.seq,
      isSkipped: true,
      hasError: false,
      isJumped: false,
    });
  });

  return answersObject;
};

export const getLastAnsweredIndex = ({
  answers,
  currentComponentAnswer,
  currentComponentIndex,
  currentLastAnsweredIndex,
}: {
  answers: ComponentAnswer[];
  currentComponentAnswer: unknown;
  currentComponentIndex: number;
  currentLastAnsweredIndex: number;
}) => {
  if (currentComponentAnswer !== null) {
    return currentComponentIndex > currentLastAnsweredIndex
      ? currentComponentIndex
      : currentLastAnsweredIndex;
  }

  let lastAnsweredIndex = -1;
  for (let i = answers.length - 1; i > -1; i--) {
    if (answers[i].answer !== null && !answers[i].isSkipped) {
      lastAnsweredIndex = i;
      break;
    }
  }
  return lastAnsweredIndex;
};

export const getProgressPercentage = ({
  lastAnsweredIndex,
  totalLength,
}: {
  lastAnsweredIndex: number;
  totalLength: number;
}) => {
  return Math.round(((lastAnsweredIndex + 1) / totalLength) * 100);
};
